import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Check } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Detect device
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    const android = /android/.test(userAgent);
    setIsIOS(ios);
    setIsAndroid(android);

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Show prompt if not standalone and not recently dismissed (show again after 7 days)
    if (!standalone && daysSinceDismissed > 7) {
      // Delay showing prompt to not interrupt initial experience
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
      return () => clearTimeout(timer);
    }

    // Listen for the beforeinstallprompt event (Chrome/Edge)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Chrome/Edge native install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else {
      // Show manual instructions for iOS/other browsers
      setShowInstructions(true);
    }
  };

  if (isStandalone || !showPrompt) return null;

  return (
    <>
      {/* Floating Install Banner */}
      <div 
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 z-50 animate-slide-up"
        data-testid="pwa-install-prompt"
      >
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-[#E91E8C] to-[#d11a7d] rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-lg mb-1">Save TYB Podcast</h3>
            <p className="text-gray-600 text-sm mb-3">
              Add to your home screen for quick access to episodes, videos & resources
            </p>
            <button
              onClick={handleInstallClick}
              className="w-full bg-[#E91E8C] hover:bg-[#d11a7d] text-white font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
              data-testid="pwa-install-button"
            >
              <Download className="w-4 h-4" />
              {deferredPrompt ? 'Install App' : 'How to Install'}
            </button>
          </div>
        </div>
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div 
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            data-testid="pwa-instructions-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-br from-[#E91E8C] to-[#d11a7d] text-white p-6 rounded-t-2xl">
              <button 
                onClick={() => setShowInstructions(false)}
                className="absolute top-4 right-4 text-white/80 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
              <h2 className="text-2xl font-bold mb-2">How to Install on Your iPhone or Android</h2>
              <p className="text-white/90 text-sm">
                The full app, free, in under 30 seconds. It sits on your home screen, opens full-screen and works exactly like an App Store app — without waiting for the App Store.
              </p>
            </div>
            
            <div className="p-6">
              {/* iOS Instructions */}
              <div className={`${isAndroid ? 'order-2 mt-8 pt-8 border-t border-gray-200' : ''}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-3xl">🍎</span>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">iPhone & iPad</h3>
                    <p className="text-gray-500 text-sm">Safari (works the same on iPad)</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#E91E8C] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                    <p className="text-gray-700 pt-1">Open <strong>Safari</strong> and visit this website</p>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#E91E8C] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                    <p className="text-gray-700 pt-1">Tap the <strong>Share button</strong> <span className="inline-block bg-gray-100 px-2 py-0.5 rounded text-lg">⬆️</span> at the bottom of the screen</p>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#E91E8C] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                    <p className="text-gray-700 pt-1">Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></p>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#E91E8C] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
                    <p className="text-gray-700 pt-1">Tap <strong>&quot;Add&quot;</strong> in the top-right corner</p>
                  </div>
                  
                  <div className="flex items-start gap-4 bg-green-50 rounded-xl p-4 mt-4">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5" />
                    </div>
                    <p className="text-green-800 pt-1">
                      <strong>Done.</strong> The TYB Podcast icon is now on your home screen. Tap it any time — it opens full-screen, just like an App Store app.
                    </p>
                  </div>
                </div>
              </div>

              {/* Android Instructions */}
              <div className={`${isIOS ? 'mt-8 pt-8 border-t border-gray-200' : ''}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-3xl">🤖</span>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">Android Phone & Tablet</h3>
                    <p className="text-gray-500 text-sm">Chrome (same steps on tablets)</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#E91E8C] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                    <p className="text-gray-700 pt-1">Open <strong>Chrome</strong> and visit this website</p>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#E91E8C] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                    <p className="text-gray-700 pt-1">Tap the <strong>3-dot menu</strong> <span className="inline-block bg-gray-100 px-2 py-0.5 rounded font-bold">⋮</span> in the top-right</p>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#E91E8C] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                    <p className="text-gray-700 pt-1">Tap <strong>&quot;Install app&quot;</strong> (or &quot;Add to Home screen&quot; on some devices)</p>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#E91E8C] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
                    <p className="text-gray-700 pt-1">Tap <strong>&quot;Install&quot;</strong> to confirm</p>
                  </div>
                  
                  <div className="flex items-start gap-4 bg-green-50 rounded-xl p-4 mt-4">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5" />
                    </div>
                    <p className="text-green-800 pt-1">
                      <strong>Done.</strong> TYB Podcast is now in your app drawer and on your home screen — opens full-screen, exactly like any other app.
                    </p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => setShowInstructions(false)}
                className="w-full mt-8 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;
